// settings/pages/PaymentMethodsPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { SettingsModal } from '../components/SettingsModal';
import * as api from '../services/settingsApi';
import {
 CreditCard, Plus, Save, ArrowLeft, Pencil, Trash2,
 Banknote, Smartphone, Building2, Globe, Wallet, MoreVertical
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface PaymentMethod {
 id: string;
 name: string;
 code: string;
 type: 'cash' | 'card' | 'mobile_money' | 'bank_transfer' | 'cheque' | 'other';
 isActive: boolean;
 requiresReference: boolean;
}

const TYPE_META: Record<string, { icon: React.ElementType; label: string; gradient: string }> = {
 cash: { icon: Banknote, label: 'Cash', gradient: 'from-emerald-500 to-green-600' },
 card: { icon: CreditCard, label: 'Card', gradient: 'from-blue-500 to-indigo-600' },
 mobile_money: { icon: Smartphone, label: 'Mobile Money', gradient: 'from-purple-500 to-violet-600' },
 bank_transfer: { icon: Building2, label: 'Bank Transfer', gradient: 'from-amber-500 to-orange-600' },
 cheque: { icon: Wallet, label: 'Cheque', gradient: 'from-rose-500 to-pink-600' },
 other: { icon: Globe, label: 'Other', gradient: 'from-slate-500 to-slate-700' },
};

export function PaymentMethodsPage() {
 const navigate = useNavigate();
 const user = useAuthStore((s) => s.user);
 const queryClient = useQueryClient();
 const addToast = useToastStore((s) => s.addToast);
 const [showForm, setShowForm] = useState(false);
 const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);
 const [form, setForm] = useState({ name: '', code: '', type: 'cash', requiresReference: false });
 const [openMenuId, setOpenMenuId] = useState<string | null>(null);

 const { data: methods, isLoading } = useQuery<PaymentMethod[]>({
 queryKey: ['payment-methods', user?.tenantId],
 queryFn: () => api.getPaymentMethods(user!.tenantId),
 enabled: !!user?.tenantId,
 });

 const saveMutation = useMutation({
 mutationFn: (payload: any) => editingMethod
 ? api.updatePaymentMethod(user!.tenantId, editingMethod.id, payload)
 : api.createPaymentMethod(user!.tenantId, payload),
 onSuccess: () => {
 queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
 addToast({ type: 'success', title: editingMethod ? 'Payment method updated' : 'Payment method created' });
 handleCloseForm();
 },
 onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
 });

 const deleteMutation = useMutation({
 mutationFn: (methodId: string) => api.deletePaymentMethod(user!.tenantId, methodId),
 onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['payment-methods'] }); addToast({ type: 'success', title: 'Deleted' }); },
 });

 const handleEdit = (method: PaymentMethod) => {
 setEditingMethod(method);
 setForm({ name: method.name, code: method.code, type: method.type, requiresReference: method.requiresReference });
 setShowForm(true);
 setOpenMenuId(null);
 };

 const handleCloseForm = () => {
 setShowForm(false);
 setEditingMethod(null);
 setForm({ name: '', code: '', type: 'cash', requiresReference: false });
 };

 if (isLoading) {
 return (
 <div className="flex items-center justify-center min-h-[60vh]">
 <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
 className="w-14 h-14 rounded-2xl border-4 border-emerald-200 border-t-emerald-600" />
 </div>
 );
 }

 const activeMethods = (methods || []).filter(m => m.isActive).length;
 const methodsWithRef = (methods || []).filter(m => m.requiresReference).length;

 return (
 <div className="min-h-screen">
 <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">

 <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
 <button onClick={() => navigate('/settings')}
 className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-600 transition-colors mb-3 group">
 <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
 Back to Settings
 </button>
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <div>
 <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
 💳 Payment Methods
 </h1>
 <p className="text-slate-500 text-sm mt-1">
 {methods?.length || 0} method{(methods?.length || 0) !== 1 ? 's' : ''} · {activeMethods} active · {methodsWithRef} require reference
 </p>
 </div>
 <Button size="md" leftIcon={<Plus size={16} />} onClick={() => { handleCloseForm(); setShowForm(true); }}>
 Add Method
 </Button>
 </div>
 </motion.div>

 {/* Stats */}
 <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
 {[
 { label: 'Total Methods', value: methods?.length || 0, gradient: 'from-indigo-500 to-blue-600' },
 { label: 'Active', value: activeMethods, gradient: 'from-emerald-500 to-teal-600' },
 { label: 'Need Reference', value: methodsWithRef, gradient: 'from-amber-500 to-orange-600' },
 { label: 'Inactive', value: (methods?.length || 0) - activeMethods, gradient: 'from-slate-500 to-slate-700' },
 ].map((stat, i) => (
 <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
 className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
 <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}>
 <CreditCard className="w-4 h-4 text-white" />
 </div>
 <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
 <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
 </motion.div>
 ))}
 </div>

 {/* Methods Grid */}
 {!methods?.length ? (
 <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
 className="text-center py-20 bg-white rounded-3xl border border-slate-200 ">
 <div className="w-20 h-20 bg-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
 <CreditCard className="w-10 h-10 text-slate-300" />
 </div>
 <h3 className="text-xl font-bold text-slate-700 ">No payment methods</h3>
 <p className="text-slate-400 mt-2 max-w-md mx-auto">Set up how customers can pay — cash, card, mobile money, bank transfers, and more.</p>
 <button onClick={() => setShowForm(true)}
 className="mt-6 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-semibold shadow-lg shadow-emerald-500/25 inline-flex items-center gap-2">
 <Plus className="w-5 h-5" /> Add First Method
 </button>
 </motion.div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {(methods || []).map((method, i) => {
 const meta = TYPE_META[method.type] || TYPE_META.other;
 const Icon = meta.icon;
 return (
 <motion.div key={method.id}
 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
 whileHover={{ y: -3 }}
 className="group relative bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
 <div className="p-5">
 <div className="flex items-start justify-between mb-3">
 <div className="flex items-center gap-3">
 <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-md`}>
 <Icon size={22} className="text-white" />
 </div>
 <div>
 <h3 className="font-semibold text-slate-900 ">{method.name}</h3>
 <code className="text-xs text-slate-400 font-mono">{method.code}</code>
 </div>
 </div>
 <div className="flex items-center gap-1.5">
 {method.requiresReference && <Badge variant="info" size="sm">Ref</Badge>}
 <Badge variant={method.isActive ? 'success' : 'danger'} size="sm">
 {method.isActive ? 'Active' : 'Inactive'}
 </Badge>
 <div className="relative">
 <button onClick={() => setOpenMenuId(openMenuId === method.id ? null : method.id)}
 className="p-1.5 rounded-lg hover:bg-slate-100 :bg-slate-700 text-slate-400">
 <MoreVertical size={14} />
 </button>
 <AnimatePresence>
 {openMenuId === method.id && (
 <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
 className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-10 py-1 w-36">
 <button onClick={() => handleEdit(method)}
 className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
 <Pencil size={13} /> Edit
 </button>
 <button onClick={() => { if (confirm(`Delete "${method.name}"?`)) deleteMutation.mutate(method.id); }}
 className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-500 hover:bg-rose-50">
 <Trash2 size={13} /> Delete
 </button>
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 </div>
 </div>
 <p className="text-sm text-slate-500 capitalize">{meta.label}</p>
 </div>
 </motion.div>
 );
 })}
 </div>
 )}

 {/* Add/Edit Modal */}
 <SettingsModal
 open={showForm}
 onClose={handleCloseForm}
 title={editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}
 subtitle="Configure accepted payment types"
 icon={CreditCard}
 footer={
 <>
 <Button variant="secondary" onClick={handleCloseForm}>Cancel</Button>
 <Button leftIcon={<Save size={16} />} onClick={() => saveMutation.mutate(form)}
 isLoading={saveMutation.isPending} disabled={!form.name || !form.code}>
 {editingMethod ? 'Update' : 'Save Method'}
 </Button>
 </>
 }
 >
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-3">
 <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cash" required />
 <Input label="Code *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="CASH" required />
 </div>
 <div>
 <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Type</label>
 <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
 className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
 <option value="cash">Cash</option>
 <option value="card">Card (POS)</option>
 <option value="mobile_money">Mobile Money</option>
 <option value="bank_transfer">Bank Transfer</option>
 <option value="cheque">Cheque</option>
 <option value="other">Other</option>
 </select>
 </div>
 <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-xl">
 <input type="checkbox" checked={form.requiresReference} onChange={(e) => setForm({ ...form, requiresReference: e.target.checked })}
 className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
 <div>
 <span className="text-sm font-medium text-slate-700 ">Requires Reference Number</span>
 <p className="text-xs text-slate-400">Staff must enter a reference when using this payment method</p>
 </div>
 </label>
 </div>
 </SettingsModal>
 </div>
 </div>
 );
}